import type { AttendanceStatus } from '@cadoan/shared'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon, type IconName } from './icons'
import './ui.css'

export { AppIcon, type IconName } from './icons'

/** The console kit: desktop density (40px controls), same tokens as the phone. */

export function Button({
  children,
  onClick,
  variant = 'secondary',
  icon,
  disabled,
  busy,
  type = 'button',
  size = 'md',
}: {
  children?: ReactNode
  onClick?: () => void
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost'
  icon?: IconName
  disabled?: boolean
  busy?: boolean
  type?: 'button' | 'submit'
  size?: 'md' | 'sm'
}) {
  return (
    <button
      type={type}
      className={`c-btn c-btn--${variant} c-btn--${size}`}
      onClick={busy ? undefined : onClick}
      disabled={disabled}
      aria-busy={busy || undefined}
    >
      <span className="c-btn__content" data-hidden={busy || undefined}>
        {icon ? <AppIcon name={icon} size={size === 'sm' ? 16 : 20} /> : null}
        {children}
      </span>
      {busy ? <span className="c-spinner" aria-hidden="true" /> : null}
    </button>
  )
}

export function IconButton({
  icon,
  label,
  onClick,
  disabled,
  tone,
}: {
  icon: IconName
  label: string
  onClick: () => void
  disabled?: boolean
  tone?: 'danger'
}) {
  return (
    <button
      type="button"
      className={`c-icon-btn ${tone === 'danger' ? 'c-icon-btn--danger' : ''}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      <AppIcon name={icon} size={20} />
    </button>
  )
}

export function Field({
  label,
  error,
  hint,
  children,
  optionalLabel,
}: {
  label: string
  error?: string | null
  hint?: string
  optionalLabel?: string
  children: (id: string) => ReactNode
}) {
  const id = useId()
  return (
    <div className="c-field">
      <label htmlFor={id} className="c-field__label t-label">
        {label}
        {optionalLabel ? <span className="c-tag t-label-sm">{optionalLabel}</span> : null}
      </label>
      {children(id)}
      {error ? (
        <div className="c-field__error t-body-sm" role="alert">
          {error}
        </div>
      ) : hint ? (
        <div className="c-field__hint t-body-sm">{hint}</div>
      ) : null}
    </div>
  )
}

export function TextInput({
  id,
  value,
  onChange,
  placeholder,
  type = 'text',
  autoComplete,
  invalid,
  autoFocus,
  onEnter,
}: {
  id?: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: 'text' | 'password' | 'date' | 'time' | 'month' | 'search'
  autoComplete?: string
  invalid?: boolean
  autoFocus?: boolean
  onEnter?: () => void
}) {
  return (
    <input
      id={id}
      className="c-input"
      type={type}
      value={value}
      placeholder={placeholder}
      autoComplete={autoComplete}
      aria-invalid={invalid || undefined}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && onEnter) {
          e.preventDefault()
          onEnter()
        }
      }}
    />
  )
}

export function PasswordInput(props: Omit<Parameters<typeof TextInput>[0], 'type'>) {
  const { t } = useTranslation()
  const [shown, setShown] = useState(false)
  return (
    <div className="c-input-wrap">
      <TextInput {...props} type={shown ? 'text' : 'password'} />
      <button
        type="button"
        className="c-input-wrap__action"
        onClick={() => setShown((v) => !v)}
        aria-label={shown ? t('passwordHide') : t('passwordShow')}
      >
        <AppIcon name={shown ? 'hide' : 'show'} size={20} />
      </button>
    </div>
  )
}

export function Select<T extends string>({
  id,
  value,
  onChange,
  options,
  ariaLabel,
  disabled,
}: {
  id?: string
  value: T
  onChange: (v: T) => void
  options: { value: T; label: string }[]
  ariaLabel?: string
  disabled?: boolean
}) {
  return (
    <select
      id={id}
      className="c-input c-select"
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value as T)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

export function Chip({
  children,
  selected,
  onClick,
}: {
  children: ReactNode
  selected?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className="c-chip t-body-sm"
      data-selected={selected || undefined}
      aria-pressed={selected}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

export const STATUS_KEY: Record<AttendanceStatus, string> = {
  present: 'statusPresent',
  excused: 'statusExcused',
  absent: 'statusAbsent',
  unmarked: 'statusUnmarked',
}

/** Short grid labels; the full word is in the cell's accessible name. */
export const STATUS_SHORT_KEY: Record<AttendanceStatus, string> = {
  present: 'statusPresentShort',
  excused: 'statusExcusedShort',
  absent: 'statusAbsentShort',
  unmarked: 'statusUnmarkedShort',
}

export function Badge({
  tone,
  children,
}: { tone: 'neutral' | 'present' | 'excused' | 'absent' | 'primary'; children: ReactNode }) {
  return <span className={`c-badge c-badge--${tone} t-label`}>{children}</span>
}

// ── Modal ────────────────────────────────────────────────────────────────────

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  width = 480,
}: {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  width?: number
}) {
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (open && !el.open) el.showModal?.()
    if (!open && el.open) el.close?.()
  }, [open])
  if (!open) return null
  return (
    <dialog
      ref={ref}
      className="c-modal"
      style={{ width }}
      aria-label={title}
      onCancel={(e) => {
        e.preventDefault()
        onClose()
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="c-modal__inner">
        <div className="c-modal__head">
          <h2 className="t-title">{title}</h2>
          <IconButton icon="close" label={title} onClick={onClose} />
        </div>
        <div className="c-modal__body">{children}</div>
        {footer ? <div className="c-modal__foot">{footer}</div> : null}
      </div>
    </dialog>
  )
}

/** Only for losing data: the title names the thing, the button repeats the verb. */
export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel,
  onConfirm,
  onClose,
  busy,
}: {
  open: boolean
  title: string
  body: ReactNode
  confirmLabel: string
  onConfirm: () => void
  onClose: () => void
  busy?: boolean
}) {
  const { t } = useTranslation()
  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      width={420}
      footer={
        <>
          <Button onClick={onClose}>{t('actionCancel')}</Button>
          <Button variant="danger" icon="delete" busy={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="t-body-sm c-muted">{body}</div>
    </Modal>
  )
}

// ── Empty, loading ───────────────────────────────────────────────────────────

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string
  body?: string
  action?: ReactNode
}) {
  return (
    <div className="c-empty">
      <div className="t-name">{title}</div>
      {body ? <div className="t-body-sm c-muted">{body}</div> : null}
      {action}
    </div>
  )
}

export function SkeletonRows({ rows = 6 }: { rows?: number }) {
  return (
    <div className="c-skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows
        <div key={i} className="c-skeleton__row" />
      ))}
    </div>
  )
}

export function ErrorBlock({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="c-error-block" role="alert">
      <AppIcon name="alert" />
      <span className="t-body-sm">{message}</span>
      <Button size="sm" icon="retry" onClick={onRetry}>
        {t('retry')}
      </Button>
    </div>
  )
}

// ── Toast ────────────────────────────────────────────────────────────────────

interface ToastState {
  id: number
  message: string
  kind: 'default' | 'error'
}

const ToastContext = createContext<(message: string, kind?: 'default' | 'error') => void>(() => {})

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const n = useRef(0)
  const show = useCallback((message: string, kind: 'default' | 'error' = 'default') => {
    if (timer.current) clearTimeout(timer.current)
    n.current += 1
    setToast({ id: n.current, message, kind })
    timer.current = setTimeout(() => setToast(null), kind === 'error' ? 5000 : 2500)
  }, [])
  return (
    <ToastContext.Provider value={show}>
      {children}
      {toast ? (
        <div key={toast.id} className={`c-toast c-toast--${toast.kind} t-body-sm`} role="status">
          {toast.message}
        </div>
      ) : null}
    </ToastContext.Provider>
  )
}

export const useToast = () => useContext(ToastContext)
